import { format } from 'date-fns';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { Border, Color, Grid, Type } from '../../../app/StyleGuide';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import { Radio, RadioListWithDescription } from '../../../components/FormElements';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../../components/ModalElements';
import curriculumService from '../../../services/curriculumService';
import { handleError } from '../../../utils/apiUtils';

type RescheduleSessionModalProps = {
  orgId: string;
  curriculumId: string;
  initiatingUserId: string;
  session: {
    isScheduled: boolean;
    availableStartDate: Date;
    scheduledDate: Date;
    availableThroughDate: Date;
    name: string;
    recommendedDate: Date;
    sessionId: string;
  };
  handleDismiss: () => void;
  getSessions: () => void;
  firstAllowedDate?: Date;
};

const SelectSessionDate = styled.div`
  label {
    cursor: default;
    color: ${Type.Color.dark};
    font-size: ${Type.Scale._1};
    font-weight: ${Type.Weight.bold};
    margin: ${Grid._4} 0 ${Grid._2};
  }
  input[type='text'] {
    font-size: ${Type.Scale._3};
    color: ${Type.Color.dark};
    border: 2px solid ${Color.Primary._50};
    border-radius: ${Border.radius};
    padding: ${Grid._3};
    :disabled {
      color: ${Type.Color.placeholder};
      background: ${Color.Gray._10};
      border-color: transparent;
    }
  }
  p {
    font-size: ${Type.Scale._1};
    margin-top: ${Grid._2};
  }
`;

const RescheduleSessionModal = ({
  orgId,
  curriculumId,
  initiatingUserId,
  session,
  handleDismiss,
  getSessions,
  firstAllowedDate,
}: RescheduleSessionModalProps) => {
  const [isScheduled, setIsScheduled] = useState(session.isScheduled);
  const firstAvailableDate =
    firstAllowedDate && firstAllowedDate > session.availableStartDate ? firstAllowedDate : session.availableStartDate;
  const [date, setDate] = useState(
    firstAvailableDate > session.scheduledDate ? firstAvailableDate : session.scheduledDate
  );
  const [isScheduling, setIsScheduling] = useState(false);

  const setSchedule = () => {
    if ((isScheduled && session.scheduledDate.getTime() !== date.getTime()) || session.isScheduled !== isScheduled) {
      setIsScheduling(true);
      curriculumService
        .setSessionSchedule(
          orgId,
          curriculumId,
          session.sessionId,
          isScheduled ? date : session.scheduledDate,
          isScheduled,
          initiatingUserId
        )
        .then(getSessions)
        .catch(handleError)
        .finally(() => {
          setIsScheduling(false);
          handleDismiss();
        });
    } else {
      handleDismiss();
    }
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Reschedule Session</h3>
      </ModalHeader>
      <ModalBody preventScroll>
        <h3>{session.name}</h3>
        {firstAvailableDate && session.availableThroughDate && (
          <p>
            Available Dates: {format(firstAvailableDate, 'MMMM d')} - {format(session.availableThroughDate, 'MMMM d')}
          </p>
        )}
        <form>
          <RadioListWithDescription>
            <li>
              <label>
                <Radio
                  data-tracking-id="scheduled"
                  name="scheduled-date"
                  onChange={() => setIsScheduled(true)}
                  checked={isScheduled}
                />
                Schedule this session
              </label>
              <SelectSessionDate>
                <label htmlFor="selected-date">Scheduled Date</label>
                <DatePicker
                  data-qa-hook="datePicker"
                  selected={date}
                  onChange={date => date && setDate(date)}
                  minDate={firstAvailableDate}
                  maxDate={session.availableThroughDate}
                  showDisabledMonthNavigation
                  disabled={!isScheduled}
                />
                <p>Suggested Date: {format(session.recommendedDate, 'MMMM d')}</p>
              </SelectSessionDate>
            </li>
            <li>
              <label>
                <Radio
                  data-tracking-id="not-scheduled"
                  name="scheduled-date"
                  onChange={() => setIsScheduled(false)}
                  checked={!isScheduled}
                />
                Don't schedule this session
              </label>
              <p>
                Not scheduling this session will remove the date and show this session as "Not Scheduled" in the list of
                sessions wherever they are shown.
              </p>
            </li>
          </RadioListWithDescription>
        </form>
      </ModalBody>
      <ModalFooter>
        <MediumPrimaryButton
          data-tracking-id="reschedule-confirm"
          onClick={() => setSchedule()}
          disabled={isScheduling}
          operating={isScheduling}
        >
          Reschedule
        </MediumPrimaryButton>
        <MediumButton onClick={() => handleDismiss()}>Cancel</MediumButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default RescheduleSessionModal;
